import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/TableTags";
import Search from "../assets/search.png";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { MdOutlineWarningAmber } from "react-icons/md";
import EllipsisTooltip from "../components/ui/EllipsisTooltip";

let DateColumns = [
  "issueS300Date",
  "lastMsForSce",
  "issueHoldout",
  "issueFinalizing",
  "coverSubInvites",
  "coverSubDue",
  "coverDueToSupplier",
  "coverSentToSupplier",
  "coverLicenseInPts",
  "coverProofApproved",
  "tocOrderFromEditors",
  "tocToSupplier",
  "issueDetailsApproved",
  "allPapersFinalized",
  "issueDeliveredToEw",
  "starFormDue",
  "starFormSubmitted",
  "speedTarget",
  "embargoExp",
  "onDate",
  "articleS300Date",
  "itemHoldout",
  "itemFinalizing",
  "copyEditTaskComplete",
  "revisedProofApproved",
  "proofsToAuthor",
  "cfcTaskComplete",
  "auProofCorrectionsSubmitted",
  "aitiTaskCompleted",
  "receivedDate",
  "acceptedDate",
  "ewImportedS5",
  "ewImportedS200",
  "sdPublishedOnWebS300",
  "revisedDate",
  "loginComplete",
  "assignedDate",
  "preAcceptDate",
  "dateBackFromSce",
  "sdPublishedOnWebS5",
  "sdPublishedOnWebS200",
  "sdPublishedOnWebS250",
  "taskDueDate",
  "taskCompletedDate",
  "dueDate",
];

const CustomTable = ({
  data = [],
  actions,
  searchTerm = "",
  setSearchTerm,
  headers = [],
  activeTab,
  onCopyeditingClick,
  sortConfig: sortConfigProp,
  setSortConfig: setSortConfigProp,
  customRender,
  displayMessage = true,
  activeRowId,
  setActiveRowId,
  rowSelectFunction,
  findEditable,
  stickyJournal = true,
  stickyVolIss = true,
  setLoading,
}) => {
  useSelector((state) => state.user);
  const [sortedData, setSortedData] = useState(data);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [columnWidths, setColumnWidths] = useState({
    journal: 0,
    pts: 0,
    volIss: 0,
  });

  useEffect(() => {
    if (!data || data.length === 0) return;

    const journalHeader = headers.find(
      (h) =>
        h.key.toLowerCase() === "journal" ||
        h.key.toLowerCase() === "journalname" ||
        h.key.toLowerCase().endsWith("journalname")
    );
    const ptsHeader = headers.find((h) => h.key.toLowerCase() === "pts");
    const volIssHeader = headers.find((h) =>
      h.key.toLowerCase().includes("voliss")
    );

    const journalWidth = Math.max(
      80,
      Math.min(
        Math.max(
          ...data.map((row) => {
            const val = getValueByPath(row, journalHeader?.key) || "";
            return String(val).length * 10;
          })
        ),
        300
      )
    );

    const ptsWidth = ptsHeader
      ? Math.max(
          90,
          Math.min(
            Math.max(
              ...data.map((row) => {
                const val = row.pts ?? "";
                return String(val).length * 10;
              })
            ),
            500
          )
        )
      : 0;

    const volIssWidth = volIssHeader
      ? Math.max(
          80,
          Math.min(
            Math.max(
              ...data.map((row) => {
                const val = row.volIss ?? "";
                return String(val).length * 10;
              })
            ),
            120
          )
        )
      : 0;

    setColumnWidths({
      journal: journalWidth,
      pts: ptsWidth,
      volIss: volIssWidth,
    });
  }, [data, headers]);

  useEffect(() => {
    if (
      sortConfigProp &&
      (sortConfigProp.key !== sortConfig.key ||
        sortConfigProp.direction !== sortConfig.direction)
    ) {
      setSortConfig(sortConfigProp);
    }
  }, [sortConfigProp]);

  useEffect(() => {
    if (sortConfig.key) {
      const sorted = data;
      setSortedData(sorted);
    } else {
      setSortedData(data);
    }
  }, [data, sortConfig]);

  const renderPtsColumn = (row, searchTerm, key) => {
    let value = "";
    let hasChanges = false;

    if (key === "pts") {
      value = row.pts;
      hasChanges = row.hasChanges;
    } else if (("articleID" || "pts" in row) && key === "volIss") {
      value = row.volIss;
    } else {
      value = row.volIss;
      hasChanges = row.hasChanges;
    }

    return (
      <div className="flex items-center gap-1">
        {hasChanges && (
          <span className="relative group">
            <MdOutlineWarningAmber className="text-[#B95000] w-[15px] h-[13px]" />
          </span>
        )}
        <span
          className="truncate"
          style={{ color: "#35424D", fontSize: "13px" }}
        >
          {highlightText(value, searchTerm)}
        </span>
      </div>
    );
  };

  const handleSort = (key) => {
    if (!key) return;

    const parsedKey = key.includes(".") ? key.split(".").pop() : key;

    let direction = "asc";
    if (sortConfig.key === parsedKey && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const newSortConfig = { key: parsedKey, direction };

    setSortConfig(newSortConfig);
    if (setSortConfigProp) setSortConfigProp(newSortConfig);
    setActiveRowId(null);
  };

  const escapeRegExp = (string) =>
    string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text, highlight) => {
    if (!highlight) return String(text ?? "");
    let textStr = text;
    if (text === false) textStr = "false";
    else if (typeof text !== "string") textStr = String(text ?? "");

    const safeHighlight = escapeRegExp(highlight);
    const regex = new RegExp(`(${safeHighlight})`, "gi");

    return textStr.split(regex).map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="bg-blue-300">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const formatDateMMDDYYYY = (dateStr) => {
    if (!dateStr) return null;
    const normalizedStr =
      dateStr.includes(" ") && !dateStr.includes("T")
        ? dateStr.replace(" ", "T")
        : dateStr;
    const date = new Date(normalizedStr);
    if (isNaN(date.getTime())) return null;
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const yyyy = date.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleActionClick = (rowId, e) => {
    e.stopPropagation();
    setActiveRowId(activeRowId === rowId ? null : rowId);
  };

  const getValueByPath = (obj, path) => {
    if (!path) return "";
    return path
      .split("?.")
      .reduce(
        (acc, part) => (acc && acc[part] !== undefined ? acc[part] : null),
        obj
      );
  };

  const renderCellContent = (row, header, isActiveCheck) => {
    if (!isActiveCheck) return renderDeactivatedCellContent(row, header);

    const fullKey = header.key;
    const key = fullKey.includes(".") ? fullKey.split(".").pop() : fullKey;
    const value = getValueByPath(row, fullKey);

    if (key === "pts" || key === "volIss") {
      return (
        <span style={header?.style ?? {}} onClick={() => header.onClick?.(row)}>
          {renderPtsColumn(row, searchTerm, key)}
        </span>
      );
    }

    if (key === "userName") {
      return (
        <span style={{ fontWeight: "600", color: "#35424D" }}>
          {highlightText(value, searchTerm)}
        </span>
      );
    }

    if (
      [
        "revisedProofRequested",
        "freeFeaturedOnline",
        "coverSubReceived",
        "sceReturnedStatus",
        "pressRelease",
        "status",
        "savedToTipsheetFolder",
        "sentFinalEmailToAuthor",
        "welcomeLetter",
        "leFiguresReady",
        "ceQcReviewComplete",
        "ptsRecordCheck",
        "sentPubDateToAuthor",
        "fastTrack",
        "revisedProofRequested",
        "sceReturnedStatus",
      ].includes(key)
    ) {
      return row[key] ? "Yes" : "No";
    }

    if (key === "email") {
      return (
        <span style={{ fontWeight: "400", color: "#35424D" }}>
          {highlightText(value, searchTerm)}
        </span>
      );
    }

    if (key === "team") {
      return (
        <>
          <i className="fas fa-users text-gray-500 mr-2"></i>
          <span style={{ color: "#35424D" }}>
            {highlightText(value, searchTerm)}
          </span>
        </>
      );
    }

    if (DateColumns.includes(key)) {
      const rawDate = value || row[key];
      if (!rawDate) return null;
      const formatted = formatDateMMDDYYYY(rawDate);
      return (
        <span className="truncate" style={{ color: "#35424D" }}>
          {highlightText(formatted, searchTerm)}
        </span>
      );
    }

    if (
      key === "title" ||
      key === "productionNotes" ||
      key === "correspondingAuthorEmail" ||
      key === "ptsRemarks" ||
      key === "ptsRefersTo" ||
      key === "url"
    ) {
      const isLong = value && value.length > 50;
      const displayValue = isLong ? value.slice(0, 50) + "…" : value;

      return (
        <div className="max-w-[300px]">
          {isLong ? (
            <EllipsisTooltip text={value}>
              <span
                className="truncate block text-[#35424D] text-[13px] font-[400]"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {highlightText(displayValue, searchTerm)}
              </span>
            </EllipsisTooltip>
          ) : (
            <span className="block text-[#35424D] text-[13px] font-[400]">
              {highlightText(displayValue, searchTerm)}
            </span>
          )}
        </div>
      );
    }

    return (
      <span
        className="truncate"
        style={{ color: "#35424D", fontSize: "13px", fontWeight: "400" }}
      >
        {highlightText(value, searchTerm)}
      </span>
    );
  };

  const renderDeactivatedCellContent = (row, header) => {
    const key = header.key;
    const value = row[key];
    if (key === "userName")
      return (
        <span className="text-gray-400" style={{ fontWeight: "600" }}>
          {highlightText(capitalizeFirstLetter(value), searchTerm)}
        </span>
      );
    if (key === "email")
      return (
        <span className="text-gray-400" style={{ fontWeight: "400" }}>
          {highlightText(value, searchTerm)}
        </span>
      );
    if (key === "team")
      return (
        <>
          <i className="fas fa-users text-gray-400 mr-2"></i>
          <span className="text-gray-400">
            {highlightText(value, searchTerm)}
          </span>
        </>
      );
    return <span className="italic text-gray-400">De-activated</span>;
  };

  if (!data || data.length === 0) {
    if (!displayMessage) return null;
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <img src={Search} alt="Search Failed" className="w-28 h-24 mb-4" />
        <p className="text-[#35424D] text-2xl mb-2">
          No results found for{" "}
          <strong>{searchTerm ? `'${searchTerm}'` : ""}</strong>
        </p>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm("");
              setLoading?.(true);
            }}
            className="mt-2 text-blue-700 text-sm hover:underline"
          >
            Clear Search
          </button>
        )}
      </div>
    );
  }

  const journalHeader = headers.find(
    (h) =>
      h.key.toLowerCase() === "journal" ||
      h.key.toLowerCase() === "journalname" ||
      h.key.toLowerCase().endsWith("journalname")
  );

  const ptsHeader = headers.find((h) => h.key.toLowerCase() === "pts");

  const volIssHeader = headers.find((h) =>
    h.key.toLowerCase().includes("voliss")
  );

  return (
    <div
      className="relative bg-white w-full flex flex-col font-[Source_Sans_Pro]"
      onClick={() => setActiveRowId(null)}
    >
      <Table
        className="w-full border-separate rounded-md"
        style={{ borderSpacing: "0" }}
      >
        <TableHeader className="sticky top-0 z-50 bg-gray-100">
          <TableRow className="border-b">
            {journalHeader && (
              <TableHead
                className={`${
                  stickyJournal ? "sticky z-[60] top-0" : ""
                } px-3 py-2 border-[#DCDCDC] font-semibold text-[11px] whitespace-nowrap bg-[#F8F8F8] text-[#35424D] cursor-pointer`}
                style={{
                  minWidth: columnWidths.journal,
                  maxWidth: columnWidths.journal,
                  ...(stickyJournal ? { left: 0 } : {}),
                }}
                onClick={() => handleSort(journalHeader.key)}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">
                    {journalHeader.value || "JOURNAL"}
                  </span>
                  {sortConfig.key === journalHeader.key.split(".").pop() && (
                    <span className="ml-1">
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
            )}

            {ptsHeader && (
              <TableHead
                className="sticky top-0 px-3 py-2 border-[#DCDCDC] font-semibold text-[11px] whitespace-nowrap bg-[#F8F8F8] text-[#35424D] z-[60] cursor-pointer"
                style={{
                  minWidth: columnWidths.pts,
                  maxWidth: columnWidths.pts,
                  left:
                    journalHeader && stickyJournal ? columnWidths.journal : 0,
                }}
                onClick={() => handleSort(ptsHeader.key)}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">{ptsHeader.value || "PTS#"}</span>
                  {sortConfig.key === ptsHeader.key && (
                    <span className="ml-1">
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
            )}

            {volIssHeader && (
              <TableHead
                className={`px-3 py-2 border-[#DCDCDC] font-semibold text-[11px] whitespace-nowrap bg-[#F8F8F8] text-[#35424D] cursor-pointer ${
                  stickyVolIss ? "sticky z-[60] top-0" : ""
                }`}
                style={{
                  minWidth: columnWidths.volIss,
                  maxWidth: columnWidths.volIss,
                  ...(stickyVolIss
                    ? {
                        left:
                          (stickyJournal ? columnWidths.journal : 0) +
                          (ptsHeader ? columnWidths.pts : 0),
                      }
                    : {}),
                }}
                onClick={() => handleSort(volIssHeader.key)}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">
                    {volIssHeader.value || "VOL/ISS"}
                  </span>
                  {sortConfig.key === volIssHeader.key && (
                    <span className="ml-1">
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
            )}

            {headers.map((header, index) => {
              const key = header.key.split("?.").pop().toLowerCase();
              if (
                key.includes("journal") ||
                key === "pts" ||
                key.includes("voliss")
              )
                return null;

              return (
                <TableHead
                  key={header.key || index}
                  className="px-3 py-2 border-[#D7D9DB] font-semibold text-[11px] cursor-pointer whitespace-nowrap sticky top-0 z-[50] bg-[#F8F8F8] text-[#35424D]"
                  onClick={() => handleSort(header.key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{header.value}</span>
                    {sortConfig.key === header.key.split(".").pop() && (
                      <span className="ml-1">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </TableHead>
              );
            })}

            {actions && (
              <TableHead className="sticky right-0 top-0 px-3 min-w-[45px] max-w-[85px] py-2 border-[#DCDCDC] font-semibold text-[11px] whitespace-nowrap bg-[#F8F8F8] text-[#35424D] z-[60]" />
            )}
          </TableRow>
        </TableHeader>

        <TableBody>
          {sortedData.map((row, rowIndex) => {
            const isActiveCheck = activeTab === "User" ? row.isActive : true;
            const rowId = row.id || row._id || rowIndex;
            const isRowActive =
              activeRowId === rowId + rowIndex && isActiveCheck;

            return (
              <TableRow
                key={rowIndex}
                className={`border-b transition-none border-[#D7D9DB] ${
                  isActiveCheck && (isRowActive || activeRowId === null)
                    ? "group hover:bg-[#EDF0FD]"
                    : ""
                } ${isRowActive ? "bg-[#EDF0FD]" : ""}`}
              >
                {journalHeader && (
                  <TableCell
                    style={{
                      cursor: findEditable?.(journalHeader.key)
                        ? "pointer"
                        : "default",
                      minWidth: columnWidths.journal,
                      maxWidth: columnWidths.journal,
                      ...(stickyJournal ? { left: 0 } : {}),
                    }}
                    onClick={() =>
                      rowSelectFunction?.(
                        row,
                        journalHeader.key,
                        journalHeader.value
                      )
                    }
                    className={`${
                      stickyJournal ? "sticky" : ""
                    } py-2 transition-none border-gray-300 h-[48px] bg-white text-[#35424D] z-10 ${
                      isActiveCheck
                        ? isRowActive
                          ? "bg-[#EDF0FD]"
                          : "group-hover:bg-[#EDF0FD]"
                        : "bg-white"
                    }`}
                  >
                    {renderCellContent(row, journalHeader, isActiveCheck)}
                  </TableCell>
                )}

                {ptsHeader && (
                  <TableCell
                    style={{
                      cursor: findEditable?.(ptsHeader.key)
                        ? "pointer"
                        : "default",
                      minWidth: columnWidths.pts,
                      maxWidth: columnWidths.pts,
                      left:
                        journalHeader && stickyJournal
                          ? columnWidths.journal
                          : 0,
                    }}
                    onClick={() =>
                      rowSelectFunction?.(row, ptsHeader.key, ptsHeader.value)
                    }
                    className={`sticky py-2 transition-none border-gray-300 h-[48px] bg-white text-[#35424D] z-10 ${
                      isActiveCheck
                        ? isRowActive
                          ? "bg-[#EDF0FD]"
                          : "group-hover:bg-[#EDF0FD]"
                        : "bg-white"
                    }`}
                  >
                    {renderCellContent(row, ptsHeader, isActiveCheck)}
                  </TableCell>
                )}

                {volIssHeader && (
                  <TableCell
                    style={{
                      cursor: findEditable?.(volIssHeader.key)
                        ? "pointer"
                        : "default",
                      minWidth: columnWidths.volIss,
                      maxWidth: columnWidths.volIss,
                      ...(stickyVolIss
                        ? {
                            left:
                              (stickyJournal ? columnWidths.journal : 0) +
                              (ptsHeader ? columnWidths.pts : 0),
                          }
                        : {}),
                    }}
                    onClick={() =>
                      rowSelectFunction?.(
                        row,
                        volIssHeader.key,
                        volIssHeader.value
                      )
                    }
                    className={`${
                      stickyVolIss ? "sticky" : ""
                    } py-2 transition-none border-gray-300 h-[48px] bg-white text-[#35424D] z-10 ${
                      isActiveCheck
                        ? isRowActive
                          ? "bg-[#EDF0FD]"
                          : "group-hover:bg-[#EDF0FD]"
                        : "bg-white"
                    }`}
                  >
                    {renderCellContent(row, volIssHeader, isActiveCheck)}
                  </TableCell>
                )}

                {headers.map((header, cellIndex) => {
                  const key = header.key.split("?.").pop().toLowerCase();
                  if (
                    key.includes("journal") ||
                    key === "pts" ||
                    key.includes("voliss")
                  )
                    return null;

                  return (
                    <TableCell
                      key={header.key || cellIndex}
                      style={{
                        cursor: findEditable?.(header.key)
                          ? "pointer"
                          : "default",
                      }}
                      onClick={() =>
                        rowSelectFunction?.(row, header.key, header.value)
                      }
                      className={`px-3 py-2 transition-none h-[48px] text-[13px] whitespace-nowrap text-[#35424D] ${
                        header.key === "userName"
                          ? "font-semibold"
                          : "font-normal"
                      } ${
                        isActiveCheck
                          ? isRowActive
                            ? "bg-[#EDF0FD]"
                            : "group-hover:bg-[#EDF0FD]"
                          : ""
                      }`}
                    >
                      {renderCellContent(row, header, isActiveCheck)}
                    </TableCell>
                  );
                })}

                {actions && (
                  <TableCell
                    className={`  sticky right-0 py-2 transition-none min-w-[45px] max-w-[85px] border-gray-300 h-[48px] bg-white text-[#35424D] z-10 ${
                      isActiveCheck
                        ? isRowActive
                          ? "bg-[#EDF0FD]"
                          : "group-hover:bg-[#EDF0FD]"
                        : "bg-white"
                    }`}
                  >
                    <div
                      className="flex items-center justify-center space-x-2"
                      onClick={(e) => handleActionClick(rowId + rowIndex, e)}
                    >
                      {actions(row, rowIndex)}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export { CustomTable };
